import Layout from "@/components/Layout";
import { Shield, Lock, UserX, Eye, FileCheck } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const Privacy = () => {
  return (
    <Layout
      seoTitle="Privacy Policy - SKECHA"
      seoDescription="Your privacy is our priority. Learn how SKECHA protects your data, handles photos, and respects your creativity. We DO NOT store your original photos."
      seoKeywords="privacy policy, data protection, photo privacy, secure uploads, SKECHA privacy"
      seoUrl="https://skecha.com/privacy"
      breadcrumbs={[
        { name: "Home", url: "https://skecha.com" },
        { name: "Privacy", url: "https://skecha.com/privacy" },
      ]}
    >
      
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="p-3 rounded-xl bg-gradient-hero shadow-glow">
                <Shield className="w-8 h-8 text-primary-foreground" />
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-hero bg-clip-text text-transparent mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Your privacy is our priority. Learn how we protect your data and respect your creativity.
            </p>
          </div>

          {/* Key Privacy Promise */}
          <Card className="border-primary/20 bg-primary/5 mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-primary">
                <UserX className="w-5 h-5" />
                Our Core Privacy Promise
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-medium text-foreground">
                🔒 <strong>We DO NOT store your original photos.</strong> Your uploaded images are processed temporarily 
                to generate coloring pages and are immediately deleted from our servers after processing.
              </p>
            </CardContent>
          </Card>

          <div className="space-y-8">
            {/* What We Collect */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  What Information We Collect
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Account Information</h4>
                  <p className="text-muted-foreground">When you create an account, we collect your email address and display name for authentication purposes.</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Usage Data</h4>
                  <p className="text-muted-foreground">We track how you use our service (pages visited, features used) to improve our platform and user experience.</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Generated Content</h4>
                  <p className="text-muted-foreground">We store the AI-generated coloring pages you create for your account access and download history.</p>
                </div>
              </CardContent>
            </Card>

            {/* What We Don't Store */}
            <Card className="border-green-500/20 bg-green-50/50 dark:bg-green-900/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-600 dark:text-green-400">
                  <Lock className="w-5 h-5" />
                  What We DON'T Store
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-green-500 mt-2 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-green-800 dark:text-green-200">Original Photos</h4>
                    <p className="text-green-700 dark:text-green-300">Your uploaded images are processed in memory and deleted immediately after coloring page generation.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-green-500 mt-2 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-green-800 dark:text-green-200">Personal Photos Content</h4>
                    <p className="text-green-700 dark:text-green-300">We never analyze, store, or share the content of your personal photos.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-green-500 mt-2 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-green-800 dark:text-green-200">Biometric Data</h4>
                    <p className="text-green-700 dark:text-green-300">We do not collect or store any biometric information from uploaded images.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* How We Use Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="w-5 h-5" />
                  How We Use Your Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Service Delivery</h4>
                  <p className="text-muted-foreground">To provide our AI coloring page generation service and manage your account.</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Communication</h4>
                  <p className="text-muted-foreground">To send important service updates and respond to your inquiries.</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Improvement</h4>
                  <p className="text-muted-foreground">To analyze usage patterns and improve our service quality and features.</p>
                </div>
              </CardContent>
            </Card>

            {/* Data Security */}
            <Card>
              <CardHeader>
                <CardTitle>Data Security</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  We implement industry-standard security measures to protect your data:
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Encrypted data transmission (HTTPS/TLS)</li>
                  <li>• Secure cloud infrastructure with regular security audits</li>
                  <li>• Limited access controls for our team members</li>
                  <li>• Regular security updates and monitoring</li>
                </ul>
              </CardContent>
            </Card>

            {/* Third Party Services */}
            <Card>
              <CardHeader>
                <CardTitle>Third-Party Services</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">Authentication</h4>
                    <p className="text-muted-foreground">We use Supabase for secure user authentication and account management.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">AI Processing</h4>
                    <p className="text-muted-foreground">Your images are processed through our AI partners with strict data deletion policies.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Analytics</h4>
                    <p className="text-muted-foreground">We use privacy-focused analytics to understand how our service is used.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Your Rights */}
            <Card>
              <CardHeader>
                <CardTitle>Your Rights</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">You have the right to:</p>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Access and download your account data</li>
                  <li>• Delete your account and all associated data</li>
                  <li>• Update your personal information</li>
                  <li>• Opt out of non-essential communications</li>
                  <li>• Request clarification about our data practices</li>
                </ul>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card>
              <CardHeader>
                <CardTitle>Contact Us</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  If you have any questions about this Privacy Policy or our data practices, 
                  please contact us at <a href="mailto:privacy@skecha.ai" className="text-primary hover:underline">privacy@skecha.com</a>
                </p>
                <p className="text-sm text-muted-foreground mt-4">
                  Last updated: January 2025
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </Layout>
  );
};

export default Privacy;